import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

export class ApiClientError extends Error {
  status?: number
  code?: string
  details?: any
  raw?: unknown

  constructor(message: string, options?: { status?: number; code?: string; details?: any; raw?: unknown }) {
    super(message)
    this.name = 'ApiClientError'
    this.status = options?.status
    this.code = options?.code
    this.details = options?.details
    this.raw = options?.raw
  }
}

function firstValidationMessage(details: any): string | null {
  if (!Array.isArray(details) || details.length === 0) return null
  const first = details[0]
  if (!first) return null
  return String(first.msg || first.message || '').trim() || null
}

function toFriendlyApiError(error: any): ApiClientError {
  if (!error || !error.response) {
    return new ApiClientError('Unable to reach the server. Please check your connection and try again.', {
      raw: error,
    })
  }

  const axiosError = error as AxiosError<any>
  const status = axiosError.response?.status
  const data: any = axiosError.response?.data || {}
  const details = data?.details
  const validationMsg = firstValidationMessage(details)
  const serverMessage = String(
    data?.message || data?.detail || data?.error || validationMsg || ''
  ).trim()

  const byStatus: Record<number, string> = {
    400: 'The request could not be processed. Please verify the input and try again.',
    401: 'Your session has expired. Please sign in again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'This action conflicts with existing data. Please refresh and try again.',
    422: validationMsg || 'Some fields are invalid. Please review the form and try again.',
    429: 'Too many requests. Please wait a moment and try again.',
    500: 'Something went wrong on our side. Please try again shortly.',
    502: 'Service is temporarily unavailable. Please try again shortly.',
    503: 'Service is temporarily unavailable. Please try again shortly.',
    504: 'The request timed out. Please try again.',
  }

  let friendly = byStatus[status || 0]
  if (!friendly) {
    friendly = serverMessage || 'The request failed. Please try again.'
  }

  // For non-sensitive 4xx messages, surface backend text when it is meaningful.
  if (status && status >= 400 && status < 500 && serverMessage && status !== 401 && status !== 403) {
    friendly = serverMessage
  }

  return new ApiClientError(friendly, {
    status,
    code: String(data?.error_code || axiosError.code || ''),
    details,
    raw: error,
  })
}

function isFormDataPayload(value: unknown): value is FormData {
  return typeof FormData !== 'undefined' && value instanceof FormData
}

function removeContentType(headers: any) {
  if (!headers) return
  if (typeof headers.delete === 'function') {
    headers.delete('Content-Type')
    headers.delete('content-type')
    return
  }
  delete headers['Content-Type']
  delete headers['content-type']
}

class ApiClient {
  private client: AxiosInstance

  constructor() {
    const resolvedBaseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api/v1'
    if (typeof window !== 'undefined') {
      console.log('[apiClient] baseURL:', resolvedBaseUrl)
    }
    this.client = axios.create({
      baseURL: resolvedBaseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = this.getAuthToken()
        console.log('[apiClient] Checking for token:', {
          url: config.url,
          hasToken: !!token
        })
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        if (isFormDataPayload(config.data)) {
          removeContentType(config.headers)
        }
        
        // Log request data for debugging
        if (config.data && !isFormDataPayload(config.data)) {
          console.log('[apiClient] Request data:', JSON.stringify(config.data, null, 2))
        }
        
        return config
      },
      (error) => Promise.reject(toFriendlyApiError(error))
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        try {
          if (typeof window !== 'undefined') {
            const url = response?.config?.url || ''
            const status = response?.status
            const dataType = response?.headers?.['content-type'] || ''
            const size = typeof response?.data === 'string' ? response.data.length : JSON.stringify(response.data || {}).length
            console.log('[apiClient] response', { url, status, dataType, size })
          }
        } catch (e) {
          // no-op
        }
        return response
      },
      async (error) => {
        const originalRequest = error.config

        // Handle 401 Unauthorized
        if (error.response?.status === 401 && !originalRequest._retry) {
          console.error('[apiClient] 401 for request:', originalRequest?.url || originalRequest?.baseURL || 'unknown')
          originalRequest._retry = true

          try {
            // Try to refresh token
            const refreshToken = this.getRefreshToken()
            if (refreshToken) {
              const response = await this.client.post('/auth/refresh', {
                refresh_token: refreshToken,
              })

              const { access_token } = response.data
              this.setAuthToken(access_token)

              // Retry original request
              originalRequest.headers.Authorization = `Bearer ${access_token}`
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.clearAuthTokens()
            if (typeof window !== 'undefined') {
              const path = window.location.pathname || ''
              if (!path.startsWith('/login') && !path.startsWith('/forgot-password') && !path.startsWith('/reset-password')) {
                console.error('[apiClient] Refresh failed, redirecting to /login')
                window.location.href = '/login'
              }
            }
            return Promise.reject(toFriendlyApiError(refreshError))
          }
        }

        return Promise.reject(toFriendlyApiError(error))
      }
    )
  }

  private getAuthToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('accessToken')
    }
    return null
  }

  private getRefreshToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('refreshToken')
    }
    return null
  }

  private setAuthToken(token: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem('accessToken', token)
    }
  }

  private clearAuthTokens(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
    }
  }

  // HTTP methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    console.log('[apiClient] GET request to:', url)
    const response = await this.client.get(url, config)
    console.log('[apiClient] GET response status:', response.status)
    return response.data
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    console.log('[apiClient] POST request to:', url)
    if (!isFormDataPayload(data)) {
      console.log('[apiClient] POST data:', JSON.stringify(data, null, 2))
    }
    const response = await this.client.post(url, data, config)
    console.log('[apiClient] POST response status:', response.status)
    return response.data
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put(url, data, config)
    return response.data
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch(url, data, config)
    return response.data
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete(url, config)
    return response.data
  }

  // File upload helper
  async uploadFile(url: string, file: File, additionalData?: Record<string, any>): Promise<any> {
    const formData = new FormData()
    formData.append('file', file)

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value as string)
      })
    }

    return this.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }
}

export const apiClient = new ApiClient()
